import { mapActions, mapGetters } from 'vuex'
import Configuration from '@/agilis/util/Configuration'
import DataTableWithGroups from '@/agilis/components/DataTableWithGroups/DataTableWithGroups.vue'
import L from 'lodash'
import WorkPackageV3Mixin from '@/applications/sales/components/WorkPackageV3/common/WorkPackageV3Mixin'
import SplittingDialog from './SplittingDialog/SplittingDialog.vue'
import Vue from 'vue'

export default {
  name: 'DeliveryGroupV2',
  mixins: [WorkPackageV3Mixin],

  props: {
    api: String,
    description: String,
    mode: String,
    trackerDTO: Object,
    deliveryAssociations: {
      type: Array,
      default: () => []
    }
  },

  components: {
    DataTableWithGroups,
    SplittingDialog
  },

  data () {
    return {
      itemsKey: 'id',
      dialog: false,
      featureId: 'deliveryGroups',
      spendingCapFeatureId: 'spendingCap',
      formTitle: this.$t('sales.label.initGroup'),
      init: true,
      items: [],
      itemsSpendingCap: [],
      wpGroup: {},
      wpGroupData: {},
      wpGroupDataSpendingCap: {},
      wp: {},
      associatedProposition: {},
      splitDialog: false,
      dataLoaded: false
    }
  },

  computed: {
    ...mapGetters({
      'applicationId': 'contextStore/applicationId',
      'loading': 'loaderStore/loading',
      'selectedContext': 'contextStore/selectedContext',
      'userId': 'userDataStore/userId',
      'trackerTypeId': 'contextStore/trackerTypeId',
      'trackerId': 'contextStore/trackerId'
    }),
    title () {
      return this.$t('sales.label.splittableWpGroups')
    },
    titleSpendingCap () {
      return this.$t('sales.label.wpGroupsSpendingCap')
    },
    configuration () {
      return Configuration.getTrackerType(this.applicationId, this.trackerTypeId).trackerTypes.deliveryGroup2.dataTable
    },
    configurationSpendingCap () {
      return Configuration.getTrackerType(this.applicationId, this.trackerTypeId).trackerTypes.deliveryGroupSpendingCap.dataTable
    },
    wpGroups () {
      return this.items.map(item => {
        return { 'name': item.name, 'id': item.deliveryGroupId }
      }).filter((value, index, self) =>
        index === self.findIndex((t) => (
          t.place === value.place && t.id === value.id
        )))
    },
    wpGroupsSpendingCap () {
      return this.itemsSpendingCap.map(item => {
        return { 'name': item.name, 'id': item.deliveryGroupId }
      }).filter((value, index, self) =>
        index === self.findIndex((t) => (
          t.place === value.place && t.id === value.id
        )))
    },
    priceListConfiguration () {
      return Configuration.getTrackerType(this.applicationId, 'priceItem').priceList
    },
    groupApi () {
      return this.$axios.agilisUrlDeliveryGroup(this.applicationId, this.trackerTypeId, this.trackerId)
    },
    splitConfiguration () {
      return Configuration.getTrackerType(this.applicationId, this.trackerTypeId).trackerTypes.deliveryGroup2.splitting
    },
    associatedPropositions () {
      const filteredAssociations = this.deliveryAssociations ? this.deliveryAssociations.filter(association => association.target.trackerTypeId === 'proposition') : []
      const propostions = filteredAssociations.map(association => association.target)
      return this.defaultSelectTitle(propostions)
    }
  },

  watch: {
    associatedPropositions: function (newValue, oldValue) {
      if (this.dataLoaded) {
        const newPropositions = newValue.map(proposition => proposition.id)
        const oldPropositions = oldValue.map(proposition => proposition.id)
        if (!L.isEqual(newPropositions, oldPropositions)) {
          this.fetchData()
        }
      }
    },
    wpGroupData (newValue) {
      if (this.wpGroupDataSpendingCap?.table?.total && newValue.table.total > this.wpGroupDataSpendingCap.table.total) {
        this.notifyWarning({ message: this.$t('message.deliverySpendingCapWarnig') })
      }
    },
    wpGroupDataSpendingCap (newValue) {
      if (this.wpGroupData?.table?.total && newValue.table.total < this.wpGroupData.table.total) {
        this.notifyWarning({ message: this.$t('message.deliverySpendingCapWarnig') })
      }
    }
  },

  methods: {
    // override ineherited of add new methods
    ...mapActions({
      notifyError: 'notificationStore/notifyError',
      notifyMessage: 'notificationStore/notifyMessage',
      notifyWarning: 'notificationStore/notifyWarning',
      startLoader: 'loaderStore/startLoader',
      stopLoader: 'loaderStore/stopLoader'
    }),
    wpGroupIsCompleted (groupingColumnId, items, group) {
      let itemGroup = items.find(item => item[groupingColumnId] === group)
      return itemGroup?.completed
    },
    async fetchData () {
      this.startLoader({ id: 'fetch-data' })
      const body = {}
      body.filterTerms = [
        {
          field: 'deliveryId',
          operator: 'EQUALS',
          value: this.trackerId
        }
      ]
      try {
        // Load group
        const url = this.$refs.dataTableWithGroups.searchApi
        const response = await this.$axios.post(url, body, this.$axios.agilisRequestConfig(url))
        const data = response.data.items

        // Fetch priceItems first
        const priceItems = await this.fetchPriceItems(data)

        // Update the table with data and priceItems, but without clannResidue
        this.items = data.map((wp) => this.decorateWpData(wp, priceItems))
        this.decorateWpSplit(this.items)
        this.$emit('wp3-group-items-update', this.items)

        // Fetch clannResidue in the background (non-blocking)
        this.fetchClannResidue(data).then(clannResidue => {
          // Once clannResidue is ready, update the table again

          this.items = L.sortBy(this.items, ['wpPropositionId', 'workpackageId'])

          const clannItems = this.mapClannItems(Object.values(clannResidue))

          this.items = this.calculateWpResidue(this.items, clannItems)

          this.decorateWpSplit(this.items)
        }).catch(error => {
          this.notifyError(error)
        })
      } catch (error) {
        this.notifyError(error)
      }
      this.dataLoaded = true
      this.stopLoader({ id: 'fetch-data' })
    },

    async fetchDataSpendingCap () {
      this.startLoader({ id: 'fetch-data' })
      const body = {}
      body.filterTerms = [
        {
          field: 'deliveryId',
          operator: 'EQUALS',
          value: this.trackerId
        }
      ]
      try {
        // Load group
        const url = this.$axios.agilisUrlQuery(this.applicationId, this.configurationSpendingCap.querySelectMode)
        const response = await this.$axios.post(url, body, this.$axios.agilisRequestConfig(url))
        const data = response.data.items

        // Fetch priceItems first
        const priceItems = await this.fetchPriceItems(data)

        // Update the table with data and priceItems, but without clannResidue
        this.itemsSpendingCap = data.map((wp) => this.decorateWpData(wp, priceItems))
        this.decorateWpSplit(this.itemsSpendingCap)
        this.$emit('wp3-group-items-spendingCap-update', this.itemsSpendingCap)
      } catch (error) {
        this.notifyError(error)
      }
      this.dataLoaded = true
      this.stopLoader({ id: 'fetch-data' })
    },

    async fetchPriceItems (workpackages) {
      // Identify the priceItems to be loaded, grouped by priceListId
      const priceLists = workpackages.reduce((acc, wp) => {
        if (wp.priceListId) {
          acc[wp.priceListId] = acc[wp.priceListId]
            ? [...acc[wp.priceListId], wp.workpackagePriceItemId]
            : [wp.workpackagePriceItemId]
        }
        if (wp.externalPriceItem) {
          acc[wp.externalPriceItem.priceListId] = acc[wp.externalPriceItem.priceListId]
            ? [...acc[wp.externalPriceItem.priceListId], wp.externalPriceItem.id]
            : [wp.externalPriceItem.id]
        }

        return acc
      }, {})

      const priceItems = {}
      try {
        // Invoke 1 query API for each priceListId filtering on the ids to load
        const responses = await Promise.all(Object.entries(priceLists).map(([priceListId, priceItemIds]) => {
          const priceListQuery = this.priceListConfiguration[priceListId]?.dataTable.querySelectMode
          if (priceListQuery) {
            const url = '/api/sales/query/' + priceListQuery
            // const queryPlaceholders = this.configuration?.workpackages?.priceItemQueryPlaceholders || {}

            const body = {
              'filterTerms': [
                { field: 'priceListId', operator: 'EQUALS', value: priceListId }, // inject priceListId filter
                { field: 'id', operator: 'IN', values: priceItemIds } // inject filter on the priceItems ites to load
              ],
              placeHolders: { priceItemTable: 'agilis_sales_workpackage3_priceitem' }
            }
            return this.$axios.post(url, body, this.$axios.agilisRequestConfig(url))
          }
          return Promise.resolve({})
        }))

        // Prepare key-value map of priceItems
        responses.forEach((response) => {
          const items = response?.data?.items
          items && Object.values(items).forEach((priceItem) => {
            priceItems[priceItem.id] = priceItem
          })
        })
      } catch (error) {
        this.notifyError(error)
      }

      return priceItems
    },

    decorateWpData (wp, priceItems) {
      const w = Object.assign({}, wp)
      // Match the wpType looking to the selected product
      const priceItem = priceItems[wp.workpackagePriceItemId]
      if (priceItem) {
        w.priceItem = priceItem
        const wpType = this.matchWpType(w)

        if (priceItem.productName) {
          Vue.set(w, 'profile', priceItem.productName)
        }

        // Decorate wp with the JOIN on the priceItems dimensions
        if (wpType.dimensions) {
          wpType.dimensions.map(d => {
            const tmp = d.wpPath.split('.')
            const value = priceItem[d.dimension]
            Vue.set(w, tmp[0], value)
          })
        }

        // decode wp group name
        w.wpType = wpType.id
        const groupId = w.groupId ? this.groupName(w.groupId) : this.defaultGroupId(w)
        Vue.set(w, 'groupId', groupId)
      }

      return w
    },

    decorateWpSplit (items) {
      const wpPropositionId = [...new Set(items.map(item => item.wpPropositionId))].filter(Boolean)
      let id = 1
      wpPropositionId.forEach(wpId => {
        if (items.filter(item => item.wpPropositionId === wpId).length > 1) {
          items.forEach(item => {
            if (item.wpPropositionId === wpId) {
              item.split = `S-${id}`
            }
          })
          const splitQuantity = items.filter(item => item.wpPropositionId === wpId).reduce(
            (accumulator, currentValue) => {
              return accumulator + currentValue.quantity
            }, 0)
          if (splitQuantity !== items.find(item => item.wpPropositionId === wpId).originalQuantity) {
            items.forEach(item => {
              if (item.wpPropositionId === wpId) {
                item.__backgroundColor = `error lighten-5`
              }
            })
            this.notifyMessage(this.$t('message.wrongQuantity'))
          }
          id++
        }
      })
    },

    mapClannItems (rawClannItems) {
      const clannItems = {}
      rawClannItems.forEach((rawClannItem) => {
        clannItems[rawClannItem.idCompitoProgetto] = {
          quantity: rawClannItem.totaleDaPianificare,
          residue: rawClannItem.totaleRimanente,
          scheduled: rawClannItem.totaleDaPianificare - rawClannItem.totaleRimanente
        }
      })
      return clannItems
    },
    calculateDeliveryWpResidue (item, clannItems) {
      const clannItem = clannItems[`DeliveryGroupWorkPackage3-${item.workpackageId}`]

      item.clannQuantity = clannItem.quantity
      item.clannResidue = clannItem.residue
    },
    calculateWpResidue (items, clannItems) {
      items.forEach(item => {
        const clannItem = clannItems[`WorkPackage3-${item.wpPropositionId}`]

        // If no proposition clann item, fallback to delivery logic
        if (!clannItem) {
          if (clannItems[`DeliveryGroupWorkPackage3-${item.workpackageId}`]) {
            this.calculateDeliveryWpResidue(item, clannItems)
          }
          return
        }

        // *** item.clannQuantity calculation ***
        // if clann quantity is greater than delivery quantity I add extra quantity on first wp
        let extraQuantity = clannItem.quantity > item.originalQuantity ? clannItem.quantity - item.originalQuantity : 0

        item.clannQuantity = extraQuantity

        item.clannQuantity += clannItem.quantity >= item.quantity ? item.quantity : clannItem.quantity

        clannItem.quantity -= item.clannQuantity
        clannItem.quantity = Math.max(clannItem.quantity, 0)

        // *** item.clannResidue calculation ***
        // I gradually transfer the clann scheduled quantity to the delivery wps residue.
        item.clannResidue = clannItem.scheduled >= item.clannQuantity ? 0 : item.clannQuantity - clannItem.scheduled

        clannItem.scheduled -= item.clannQuantity
        clannItem.scheduled = Math.max(clannItem.scheduled, 0)

        // if clann scheduled quantity is greater than delivery quantity (negative residue) I add extra scheduled quantity on last wp
        if (clannItem.quantity === 0 && clannItem.scheduled > 0) {
          item.clannResidue -= clannItem.scheduled
        }
      })

      return items
    },

    async fetchClannResidue (workpackages) {
      const sequenceNumbers = L.uniq(
        workpackages.map(workpackage => {
          return workpackage.sequenceNumber
        })
      )
      let data = {}
      try {
        // find clann wp info (quantity, residue) by workpackageId
        // clannResidue is an object defined like:
        // {
        //    WorkPackage3-1: {...},
        //    WorkPackage3-2: {...},
        //    WorkPackage3-3: {...}
        // }
        // where WorkPackage3-1 is the clann idCompitoProgetto based on arpa workpackageId
        // The clannWp is an object like:
        // {
        //   "totaleRimanente": 1.0,
        //   "totaleDaPianificare": 1.0,
        //   "unitaDiMisura": 0,
        //   "idCompitoProgetto": "WorkPackage3-1",
        //   "clannWpId": 123
        // }
        const responses = await Promise.all(sequenceNumbers.map(sequenceNumber => {
          if (sequenceNumber) {
            const url = this.$axios.agilisUrlClannResidue(sequenceNumber)
            return this.$axios.get(url, this.$axios.agilisRequestConfig(url))
          }
          return Promise.resolve({})
        }))
        responses.forEach((response) => {
          const items = response?.data
          items && items.forEach((item) => {
            data[item.idCompitoProgetto] = item
          })
        })
      } catch (error) {
        this.notifyError(error)
      }
      return data
    },

    defaultSelectTitle (propositions) {
      const titles = propositions.map(proposition => {
        return Object.assign(proposition, { fullTitle: proposition.sequenceNumber + ' - ' + proposition.title })
      })
      return titles
    },

    closeDialog () {
      this.dialog = false
      this.init = true
      this.formTitle = this.$t('sales.label.initGroup')
      this.wp = {}
      this.wpGroup = {}
      this.associatedProposition = {}
    },

    initGroup (proposition) {
      this.startLoader({ id: 'init-group' })
      const url = this.$axios.agilisUrlDeliveryGroupInit(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        proposition.id)
      Vue.axios
        .post(
          url,
          {},
          Vue.axios.agilisRequestConfig(url))
        .then(() => {
          this.fetchData()
          this.closeDialog()
        })
        .catch((error) => {
          if (error.response.data.error.exceptionDetails.includes('agilis_sales_delivery_group_wp.unique_workPackage3')) {
            this.notifyError({ message: this.$t('error.propositionMappedTwice') })
          } else {
            this.notifyError(error)
          }
        })
        .finally(() => {
          this.stopLoader({ id: 'init-group' })
        })
    },

    async editWPGroup (group) {
      this.startLoader({ id: 'edit-group' })
      let deliveryGroupId = group.id
      const url = this.$axios.agilisUrlDeliveryGroupV2WpMove(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        deliveryGroupId,
        this.wp.workpackageId)

      Vue.axios.put(url, {}, Vue.axios.agilisRequestConfig(url))
        .then(() => {
          this.fetchData()
          this.closeDialog()
        })
        .catch((error) => {
          this.notifyError(error)
        })
      this.stopLoader({ id: 'edit-group' })
    },

    editItem (item) {
      this.dialog = true
      this.init = false
      const title = this.$t('sales.label.editWPGroup', { wpTitle: item.title })
      this.formTitle = Configuration.i18n.capitalizePhrase(title)
      this.wp = item
    },

    userCanAccessFeature (featureId) {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, featureId, null, this.trackerDTO, this.userId)
    },

    userCanDoAndAccessFeature (feature, operation) {
      return Configuration.auth.userCanDoAndAccessFeature(this.selectedContext, feature, operation, this.trackerDTO, this.userId)
    },

    requestWpGroupDataSpendingCapReload (wpGroupDataSpendingCap) {
      this.wpGroupDataSpendingCap = wpGroupDataSpendingCap
      this.$emit('group-data-spendingCap-update', wpGroupDataSpendingCap)
    },

    requestWpGroupDataReload (wpGroupData) {
      this.wpGroupData = wpGroupData
      this.$emit('group-data-update', wpGroupData)
    }
  }
}
